import { createSelector } from "reselect";
import { getProjectById, getProjectMembersById } from "./selector.projects.jsx";
import { getActiveUsers, getAllUsers, getLoggedUser } from "./selector.users.jsx";

export const getAllInvoices = (state) => state.subscription.invoices;

export const getAllInvoicesSorted = createSelector(getAllInvoices, (invoices) => {
  return Object.values(invoices).sort((a, b) => {
    return b.issued_on - a.issued_on;
  });
});

export const getLastInvoice = createSelector(getAllInvoicesSorted, (invoices) => invoices[0]);

export const getInvoiceById = (state, id) => getAllInvoices(state)[id];

export const getSubscription = (state) => state.subscription;

export const getTasksSubscriptions = createSelector(getSubscription, (subscriptions) => subscriptions.tasks);

export const getTaskSubscriberIds = createSelector(
  getTasksSubscriptions,
  (state, taskId) => taskId,
  (tasks, taskId) => {
    if (tasks && tasks[taskId]) {
      return tasks[taskId];
    }
    return [];
  }
);

export const getNotesSubscriptions = createSelector(getSubscription, (subscriptions) => subscriptions.notes);

export const getNoteSubscriberIds = createSelector(
  getNotesSubscriptions,
  (state, noteId) => noteId,
  (notes, noteId) => {
    if (notes[noteId]) {
      return notes[noteId];
    }
    return [];
  }
);

export const getSubscriberIdsByTypeAndId = createSelector(
  getSubscription,
  (state, parentType) => parentType,
  (state, parentType, parentId) => parentId,
  (allSubscriptions, parentType, parentId) => {
    let resolvedKey;
    switch (parentType) {
      case "Task":
        resolvedKey = "tasks";
        break;
      case "RecurringTask":
        resolvedKey = "recurringTasks";
        break;
      case "Note":
        resolvedKey = "notes";
        break;
      case "Discussion":
        resolvedKey = "discussions";
        break;
      case "CalendarEvent":
        resolvedKey = "calendarEvents";
        break;
      case "ProjectTemplateTask":
        resolvedKey = "projectTemplateTasks";
        break;
      case "ProjectTemplateRecurringTask":
        resolvedKey = "projectTemplateRecurringTasks";
        break;
      case "ProjectTemplateNote":
        resolvedKey = "projectTemplateNotes";
        break;
      case "ProjectTemplateDiscussion":
        resolvedKey = "projectTemplateDiscussions";
        break;
      default:
        return [];
    }

    const mapOfObjectIds = allSubscriptions[resolvedKey];

    let listOfSubscriberIds = [];
    if (mapOfObjectIds && mapOfObjectIds[`${parentId}`] && Array.isArray(mapOfObjectIds[`${parentId}`])) {
      listOfSubscriberIds = mapOfObjectIds[`${parentId}`];
    }

    return listOfSubscriberIds;
  }
);

export const getSortedSubscribersByProjectId = createSelector(
  getProjectMembersById,
  getAllUsers,
  getLoggedUser,
  (members, users, user) => {
    const allUsers = users.toMap().toList().toJS();
    return allUsers
      .reduce((prev, next) => {
        if (next.class === "Owner" && user.class === "Owner") {
          prev.push(next);
        } else if (members.includes(next.id) || next.class === "Owner") {
          prev.push(next);
        }
        return prev;
      }, [])
      .sort((a, b) => {
        return a.display_name.localeCompare(b.display_name);
      });
  }
);

export const getPossibleSubscribers = createSelector(
  getActiveUsers,
  getLoggedUser,
  (state, members) => members,
  (users, user, members) => {
    return users
      .toMap()
      .toList()
      .toJS()
      .reduce((prev, next) => {
        if (next.class === "Owner" && user.class === "Owner") {
          prev.push(next);
        } else if (members.includes(next.id) || next.class === "Owner") {
          prev.push(next);
        }
        return prev;
      }, [])
      .sort((a, b) => {
        if (a.display_name && b.display_name) {
          return a.display_name.localeCompare(b.display_name);
        }
        return a.email.localeCompare(b.email);
      });
  }
);
export const getProjectSubscriberIds = createSelector(
  getLoggedUser,
  getAllUsers,
  getProjectById,
  (loggedUser, users, project) => {
    if (loggedUser.get("class") !== "Client") {
      return project.get("subscribers").toSet().add(loggedUser.id).toJS();
    } else {
      if (project?.get("leader_id") > 0) {
        return [loggedUser.id, project.get("leader_id")];
      } else {
        return project
          .toJS()
          .members.filter(
            (memberId) => users.get(`${memberId}`).get("class") !== "Client" || memberId === loggedUser.id
          );
      }
    }
  }
);

export const getProjectSubscribers = createSelector(getLoggedUser, getProjectById, (loggedUser, project) => {
  if (project) {
    return project.get("subscribers").toSet().add(loggedUser.id).toJS();
  }
  return [loggedUser.id];
});

export const getParentEntity = createSelector(
  (state) => state.tasks,
  (state) => state.notes,
  (state) => state.discussions,
  (state) => state.calendarEvents,
  (state) => state.project_template_elements,
  (state, parentType) => parentType,
  (state, _, parentId) => parentId,
  (tasks, notes, discussions, events, projectTemplateElements, parentType, parentId) => {
    if (parentType === "CalendarEvent") {
      return events[parentId];
    } else if (parentType === "Task") {
      return tasks.get(`${parentId}`);
    } else if (parentType === "Note") {
      return notes[parentId];
    } else if (parentType === "Discussion") {
      return discussions[parentId];
    } else if (
      parentType === "ProjectTemplateTask" ||
      parentType === "ProjectTemplateRecurringTask" ||
      parentType === "ProjectTemplateNote" ||
      parentType === "ProjectTemplateDiscussion"
    ) {
      return projectTemplateElements[parentId];
    }
  }
);
